/**
 * Automatische Sicherung in einen Ordner (F-46) - rein und getestet.
 *
 * Ohne Dexie, ohne DOM: Dateinamen, Aufraeumliste, Faelligkeit und die
 * Pruefung der gespeicherten Werte. Das Schreiben steht in
 * `lib/backup/autoSicherung.ts`, der Ablauf in
 * `modules/settings/autoSicherungDienst.ts`.
 */
import type { LocalDate, LocalDateTime } from '@/domain/types';
import { addDays, differenceInDays } from '@/lib/datetime';

/** Einmal pro Woche (Roadmap 3, F-46). */
export const AUTO_INTERVALL_TAGE = 7;

/** Wie viele automatische Sicherungen im Ordner bleiben. */
export const BEHALTEN_OPTIONEN = [3, 6, 12] as const;
export const BEHALTEN_STANDARD = 6;

/**
 * Hat eine neue Sicherung weniger als diesen Anteil der Eintraege der
 * vorigen, wird nicht aufgeraeumt: Nach einem versehentlich falschen Import
 * oder einer geleerten Datenbank sollen die guten alten Kopien nicht nach und
 * nach aus dem Ordner rotieren.
 */
export const MIN_ANTEIL_EINTRAEGE = 0.5;

/**
 * Eigener Namensanfang, getrennt von der Sicherung von Hand
 * (`ole-privat-sicherung-YYYY-MM-DD.json`). Das Muster passt dadurch nie auf
 * eine Datei von Hand, eine halb geschriebene `.crswap`-Datei oder eine fremde
 * Datei - und nur passende Dateien werden je geloescht.
 */
export const AUTO_MUSTER = /^ole-privat-sicherung-auto-(\d{4})-(\d{2})-(\d{2})-(\d{2})(\d{2})\.json$/;

/** Was nach der letzten automatischen Sicherung gespeichert wird. */
export interface AutoZuletzt {
  /** Zeitpunkt (ISO-Instant), gleich `exportedAt` der Datei. */
  at: string;
  datei: string;
  bytes: number;
  /** Summe aller Eintraege dieser Sicherung. */
  eintraege?: number;
  /**
   * Eintraege des letzten vertrauenswuerdigen Stands - der Vergleichswert fuer
   * die naechste Sicherung. Waehrend des Schutzes bleibt er stehen; sonst
   * raeumte schon die zweite kleine Sicherung die guten Kopien weg.
   */
  referenz?: number;
  /** Ohne Eintragszahl (Datenbank geleert): Groesse der groessten Kopie im Ordner. */
  referenzBytes?: number;
  /** Aufraeumen wurde ausgelassen, weil es deutlich weniger Eintraege waren. */
  geschuetzt?: boolean;
}

/**
 * `'2026-10-05T18:30'` -> `'ole-privat-sicherung-auto-2026-10-05-1830.json'`.
 * Ortszeit, nie UTC; alphabetisch sortiert ist damit auch zeitlich sortiert.
 */
export function autoDateiname(zeit: LocalDateTime): string {
  const tag = zeit.slice(0, 10);
  const uhr = zeit.slice(11, 13) + zeit.slice(14, 16);
  return `ole-privat-sicherung-auto-${tag}-${uhr}.json`;
}

export function istAutoDatei(name: string): boolean {
  return AUTO_MUSTER.test(name);
}

/** Zeitpunkt aus dem Dateinamen, z. B. fuer die Tabelle; sonst `null`. */
export function zeitAusDateiname(name: string): LocalDateTime | null {
  const m = AUTO_MUSTER.exec(name);
  return m ? `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}` : null;
}

/**
 * Welche Dateien weg duerfen: nur automatische, die neuesten `behalten`
 * bleiben. `neu` (die eben geschriebene) bleibt immer und zaehlt mit - auch
 * wenn neuere Namen da sind (Zeitzone gewechselt, Uhr falsch, Zeitumstellung).
 * Fremde Dateien und Sicherungen von Hand tauchen nie in der Liste auf.
 */
export function zuLoeschen(namen: readonly string[], behalten: number, neu?: string): string[] {
  const n = Math.max(1, Math.floor(behalten));
  const andere = namen.filter((x) => istAutoDatei(x) && x !== neu).sort().reverse();
  return andere.slice(neu && namen.includes(neu) ? n - 1 : n);
}

/**
 * Aufraeumliste aus dem Ordnerinhalt. Leere automatische Dateien (Schreiben
 * abgebrochen, z. B. bei vollem Speicher) zaehlen NICHT als Kopie - sonst
 * koennten sie gute alte Sicherungen verdraengen - und kommen gleich mit weg.
 */
export function aufraeumListe(dateien: readonly { name: string; bytes: number }[], behalten: number, neu?: string): string[] {
  const eigene = dateien.filter((d) => istAutoDatei(d.name));
  const leer = eigene.filter((d) => d.bytes === 0 && d.name !== neu).map((d) => d.name);
  const voll = eigene.filter((d) => d.bytes > 0 || d.name === neu).map((d) => d.name);
  return [...leer, ...zuLoeschen(voll, behalten, neu)];
}

/**
 * Ohne bisherige automatische Sicherung immer faellig. Ein letzter Stand in
 * der Zukunft (Uhr ging einmal vor) zaehlt ebenfalls als faellig - sonst
 * schliefe die Sicherung bis zu diesem Datum ein.
 */
export function istFaellig(letzterTag: LocalDate | null, heute: LocalDate, intervall = AUTO_INTERVALL_TAGE): boolean {
  return letzterTag === null || letzterTag > heute || differenceInDays(letzterTag, heute) >= intervall;
}

/** Ab wann die naechste automatische Sicherung faellig ist. */
export function naechsteAb(letzterTag: LocalDate, intervall = AUTO_INTERVALL_TAGE): LocalDate {
  return addDays(letzterTag, intervall);
}

/**
 * Deutlich weniger Eintraege als bei der vorigen automatischen Sicherung?
 * Ohne Vergleichswert (erste Sicherung, aelterer Stand) nie.
 */
export function deutlichWeniger(neu: number, vorher: number | undefined | null): boolean {
  return typeof vorher === 'number' && vorher > 0 && neu < vorher * MIN_ANTEIL_EINTRAEGE;
}

export interface Schutz {
  /** Nicht aufraeumen - die neue Sicherung ist deutlich kleiner als der Vergleichsstand. */
  geschuetzt: boolean;
  /** Vergleichswert fuer die naechste Sicherung (siehe `AutoZuletzt`). */
  referenz?: number;
  referenzBytes?: number;
}

/**
 * Schutz vor dem Verdraengen guter Kopien. Verglichen wird mit dem letzten
 * vertrauenswuerdigen Stand - solange geschuetzt wird, bleibt er stehen, bis
 * wieder mindestens die Haelfte erreicht ist oder Ole den neuen Stand
 * bestaetigt (`schutzAufheben`). Fehlt jeder gespeicherte Vergleich (erste
 * Sicherung, Datenbank geleert), dient die groesste vorhandene automatische
 * Sicherung im Ordner (`groessteAndere`, in Bytes) als Mass.
 */
export function schutzPruefen(neu: { eintraege: number; bytes: number }, vorher: AutoZuletzt | null, groessteAndere: number | null): Schutz {
  const refEintraege = vorher?.referenz ?? (vorher?.geschuetzt ? undefined : vorher?.eintraege);
  if (refEintraege !== undefined) {
    return deutlichWeniger(neu.eintraege, refEintraege)
      ? { geschuetzt: true, referenz: refEintraege }
      : { geschuetzt: false, referenz: neu.eintraege };
  }
  const refBytes = vorher?.referenzBytes ?? groessteAndere ?? undefined;
  return deutlichWeniger(neu.bytes, refBytes)
    ? { geschuetzt: true, referenzBytes: refBytes! }
    : { geschuetzt: false, referenz: neu.eintraege };
}

/** "Neuer Stand stimmt": die letzte Sicherung wird zum Vergleichswert, aufgeraeumt wird wieder. */
export function schutzAufheben(z: AutoZuletzt): AutoZuletzt {
  const { geschuetzt: _g, referenzBytes: _b, ...rest } = z;
  return { ...rest, ...(z.eintraege !== undefined ? { referenz: z.eintraege } : {}) };
}

/** Gespeicherten Wert pruefen (Systemgrenze: `meta.value` ist `unknown`). */
export function behaltenAus(roh: unknown): number {
  return BEHALTEN_OPTIONEN.find((n) => n === roh) ?? BEHALTEN_STANDARD;
}

/** Gespeicherten Wert pruefen; alles Unerwartete -> `null`. */
export function zuletztAus(roh: unknown): AutoZuletzt | null {
  if (typeof roh !== 'object' || roh === null) return null;
  const z = roh as Record<string, unknown>;
  if (typeof z.at !== 'string' || Number.isNaN(Date.parse(z.at))) return null;
  if (typeof z.datei !== 'string' || typeof z.bytes !== 'number') return null;
  const ergebnis: AutoZuletzt = { at: z.at, datei: z.datei, bytes: z.bytes };
  if (typeof z.eintraege === 'number' && Number.isFinite(z.eintraege)) ergebnis.eintraege = z.eintraege;
  if (typeof z.referenz === 'number' && Number.isFinite(z.referenz)) ergebnis.referenz = z.referenz;
  if (typeof z.referenzBytes === 'number' && Number.isFinite(z.referenzBytes)) ergebnis.referenzBytes = z.referenzBytes;
  if (z.geschuetzt === true) ergebnis.geschuetzt = true;
  return ergebnis;
}
